import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import { colors, spacing, radius, shadows, focusVisible } from '@/styles/designTokens'

// ---------------------------------------------------------------------------
// Desktop sidebar — fills the sticky wrapper height; items list scrolls inside.
// Visible only at lg+; breakpoint controlled by CartSidebarSticky in MenuLayout.
// ---------------------------------------------------------------------------
export const DesktopSidebar = styled(Box)({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	overflow: 'hidden',
	borderRadius: radius.sheet,
	border: `1px solid ${colors.border}`,
	boxShadow: shadows.card,
	backgroundColor: colors.surfaceWhite,
})

// ---------------------------------------------------------------------------
// Tablet — full-viewport dimmer; always in DOM, toggled via visibility.
// Click on dimmer closes the drawer; click on modal stops propagation.
// ---------------------------------------------------------------------------
export const TabletDimmer = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ isOpen }) => ({
	position: 'fixed',
	inset: 0,
	backgroundColor: colors.overlayBackdrop,
	zIndex: 1300, // MUI modal z-index
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	visibility: isOpen ? 'visible' : 'hidden',
	pointerEvents: isOpen ? 'auto' : 'none',
}))

export const TabletModal = styled(Box)({
	width: '100%',
	maxWidth: 400,
	backgroundColor: colors.surfaceWhite,
	borderRadius: radius.sheet,
	padding: spacing.lg,
})

// Tablet bottom sticky bar — visible only when drawer is closed.
// Native <button> for correct keyboard/focus/screen reader behaviour.
export const TabletStickyBar = styled('button', {
	shouldForwardProp: (prop) => prop !== 'isOpen',
})<{ isOpen: boolean }>(({ isOpen }) => ({
	position: 'fixed',
	bottom: 0,
	left: '50%',
	transform: 'translateX(-50%)',
	width: 400,
	backgroundColor: colors.surfaceWhite,
	borderRadius: radius.sheet,
	border: `1px solid ${colors.brand}`,
	paddingLeft: spacing.sm,
	paddingRight: spacing.sm,
	paddingTop: spacing.lg,
	paddingBottom: spacing.lg,
	cursor: 'pointer',
	zIndex: 1200, // MUI appBar z-index
	fontFamily: 'inherit',
	visibility: isOpen ? 'hidden' : 'visible',
	pointerEvents: isOpen ? 'none' : 'auto',
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
}))

// ---------------------------------------------------------------------------
// Mobile — full-width bottom sheet (open) or sticky bar (closed).
// Both are native <button> elements for correct keyboard/focus behaviour.
// ---------------------------------------------------------------------------

// Bottom sheet — slides up over content when drawer is open.
export const MobileSheet = styled(Box)({
	position: 'fixed',
	bottom: 0,
	left: 0,
	right: 0,
	backgroundColor: colors.surfaceWhite,
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	zIndex: 1300, // MUI modal z-index
	padding: spacing.lg,
})

// Visually-hidden close button inside the sheet — screen-reader only.
// Positioned off-screen so it doesn't disrupt the visual swipe-handle design,
// but is the first focusable element for keyboard/AT users (WCAG 2.1 AA focus target).
export const CloseSheetButton = styled('button')({
	position: 'absolute',
	width: 1,
	height: 1,
	padding: 0,
	margin: -1,
	overflow: 'hidden',
	clip: 'rect(0,0,0,0)',
	whiteSpace: 'nowrap',
	border: 0,
	'&:focus-visible': {
		position: 'static',
		width: 'auto',
		height: 'auto',
		margin: 0,
		overflow: 'visible',
		clip: 'auto',
		whiteSpace: 'normal',
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

// Drag handle pill — Figma-spec 80×8 pill at the top of the sheet.
// radius.card (8px) is the closest token; no exact 6px token exists.
export const SwipeHandle = styled(Box)({
	width: 80,
	height: 8,
	borderRadius: radius.card,
	backgroundColor: colors.overlaySubtle,
	marginLeft: 'auto',
	marginRight: 'auto',
	marginBottom: spacing.xl,
})

// Full-width sticky bar — sits at the bottom when the sheet is closed.
export const MobileStickyBar = styled('button')({
	position: 'fixed',
	bottom: 0,
	left: 0,
	right: 0,
	backgroundColor: colors.surfaceWhite,
	borderRadius: `${radius.sheet}px ${radius.sheet}px 0 0`,
	border: `1px solid ${colors.border}`,
	padding: spacing.lg,
	cursor: 'pointer',
	zIndex: 1200, // MUI appBar z-index
	fontFamily: 'inherit',
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.brand}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})
